interface ShamsaProps {
  /** Pixel size — the SVG is square. Default 240. */
  size?: number;
  className?: string;
}

/**
 * Shamsa (شمسه) — the radiant medallion of Persian manuscript illumination.
 *
 * Draws a classical 8-point khatam star inside a double ring, a petaled
 * rosette at the core, and eight gold accent dots marking the cardinal and
 * diagonal axes. Structural strokes inherit `currentColor` so the mark
 * flows with parchment/midnight themes; the accent dots lock to the
 * saddle-brown token used everywhere else on the site.
 *
 * Designed to sit as a low-opacity seal behind hero/contact copy — the
 * caller controls opacity via `className` (e.g. `opacity-[0.07]`).
 */
export function Shamsa({ size = 240, className }: ShamsaProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 240 240"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
      className={className}
    >
      {/* Mechanical-watch composition: the bezel and pivot are fixed,
       *  the medallion rings and the rosette petals rotate at different
       *  rates and opposite directions. Reads as "internals of a
       *  mechanical watch" — static case, live mechanism inside.
       *  `prefers-reduced-motion` kills all rotation via CSS. */}
      <g transform="translate(120 120)">
        {/* ---- BEZEL (static) ---- */}
        {/* Outer double ring — the watch case */}
        <circle r="118" strokeWidth="0.4" opacity="0.5" />
        <circle r="113" strokeWidth="0.8" />
        <circle r="108" strokeWidth="0.4" opacity="0.5" />

        {/* Khatam 8-point star — two overlapping squares, the hour
         *  markers. Fixed positions relative to the bezel. */}
        <rect x="-80" y="-80" width="160" height="160" strokeWidth="0.7" />
        <rect
          x="-80"
          y="-80"
          width="160"
          height="160"
          strokeWidth="0.7"
          transform="rotate(45)"
        />

        {/* Gold accent dots at the 8 points of the khatam. These sit on
         *  the *bezel* (the hour markers), so they stay fixed too —
         *  only the opacity breathes. */}
        <g fill="var(--color-accent)" stroke="none" className="shamsa-dots">
          <circle cx="0" cy="-80" r="1.8" />
          <circle cx="57" cy="-57" r="1.8" />
          <circle cx="80" cy="0" r="1.8" />
          <circle cx="57" cy="57" r="1.8" />
          <circle cx="0" cy="80" r="1.8" />
          <circle cx="-57" cy="57" r="1.8" />
          <circle cx="-80" cy="0" r="1.8" />
          <circle cx="-57" cy="-57" r="1.8" />
        </g>

        {/* ---- MECHANISM (rotating) ---- */}
        {/* Inner medallion rings — the "minute wheel." Slow clockwise
         *  rotation; 300s/rev ≈ 1.2°/s. */}
        <g className="shamsa-spin-slow">
          <circle r="52" strokeWidth="0.8" />
          <circle r="46" strokeWidth="0.4" opacity="0.6" />
        </g>

        {/* 8-petal rosette — the "balance wheel." Faster counter-clockwise
         *  rotation; 120s/rev ≈ 3°/s. Opposite direction from the minute
         *  wheel so the composition reads as geared, not uniform. */}
        <g
          strokeWidth="0.5"
          opacity="0.75"
          className="shamsa-spin-fast-reverse"
        >
          <ellipse cx="0" cy="-30" rx="5" ry="14" />
          <ellipse cx="0" cy="-30" rx="5" ry="14" transform="rotate(45)" />
          <ellipse cx="0" cy="-30" rx="5" ry="14" transform="rotate(90)" />
          <ellipse cx="0" cy="-30" rx="5" ry="14" transform="rotate(135)" />
          <ellipse cx="0" cy="-30" rx="5" ry="14" transform="rotate(180)" />
          <ellipse cx="0" cy="-30" rx="5" ry="14" transform="rotate(225)" />
          <ellipse cx="0" cy="-30" rx="5" ry="14" transform="rotate(270)" />
          <ellipse cx="0" cy="-30" rx="5" ry="14" transform="rotate(315)" />
        </g>

        {/* Center pivot — the watch's jewel. Fixed. */}
        <circle r="3" fill="var(--color-accent)" stroke="none" />
      </g>
    </svg>
  );
}

interface GirihStarProps {
  size?: number;
  className?: string;
}

/**
 * Girih (گره) — the 8-point geometric star that anchors Persian and Islamic
 * architectural tilework. This is the minimalist outline variant: two
 * overlapping squares + a small inner ring, nothing else. Use inline as a
 * section-divider ornament or as a subtle seal.
 */
export function GirihStar({ size = 22, className }: GirihStarProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="-12 -12 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="0.6"
      aria-hidden="true"
      className={className}
    >
      <rect x="-8" y="-8" width="16" height="16" />
      <rect x="-8" y="-8" width="16" height="16" transform="rotate(45)" />
      <circle r="2.2" strokeWidth="0.5" />
      <circle r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

interface CrescentProps {
  size?: number;
  className?: string;
}

/**
 * Waxing crescent with a single nearby star — a common Persian manuscript
 * marginalia motif. We use it where a small celestial accent reinforces
 * the "Cup of Jamshid shows the cosmos" brand thread without committing a
 * whole shamsa's worth of ornament.
 */
export function Crescent({ size = 40, className }: CrescentProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 40 40"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="0.8"
      aria-hidden="true"
      className={className}
    >
      {/* Crescent = large ring masked by an offset ring. Drawn as two
       *  arcs (outer + inner) so the stroke reads cleanly on parchment. */}
      <path d="M 27 8 A 14 14 0 1 0 27 32 A 11 14 0 1 1 27 8 Z" />
      {/* Companion star — a tiny 4-point burst placed near the crescent's
       *  upper-right side to balance the silhouette. */}
      <g transform="translate(33 12)" strokeWidth="0.6">
        <line x1="0" y1="-2.2" x2="0" y2="2.2" />
        <line x1="-2.2" y1="0" x2="2.2" y2="0" />
        <circle r="0.6" fill="currentColor" stroke="none" />
      </g>
    </svg>
  );
}

interface SectionDividerProps {
  /** Center ornament — defaults to a girih star. */
  variant?: 'girih' | 'diamond';
  /** Max width in px of the flanking rules. */
  ruleMaxWidth?: number;
  className?: string;
}

/**
 * Thin ornamented rule that separates marketing sections. Two short rules
 * flanking a single Persian motif — echoes the folio-divider convention of
 * classical manuscripts without becoming decoration-for-decoration's-sake.
 *
 * RTL handling: the gradient keyword `to right` is physical, not logical,
 * so it doesn't flip under `dir="rtl"`. To keep "the start-side rule fades
 * in toward the motif" true in both directions, we force a `row-reverse`
 * in RTL via the `rtl:` custom-variant — that flips child order back to
 * visual LTR, so the hard-coded gradient directions land on the right
 * physical spans in both scripts.
 */
export function SectionDivider({
  variant = 'girih',
  ruleMaxWidth = 120,
  className,
}: SectionDividerProps) {
  return (
    <div
      aria-hidden="true"
      className={
        'flex items-center justify-center gap-4 opacity-45 rtl:flex-row-reverse ' +
        (className ?? '')
      }
      style={{ color: 'var(--color-accent)' }}
    >
      <span
        className="block h-px flex-1"
        style={{
          maxWidth: ruleMaxWidth,
          background:
            'linear-gradient(to right, transparent, currentColor)',
        }}
      />
      {variant === 'girih' ? (
        <GirihStar size={18} />
      ) : (
        <Diamond size={10} />
      )}
      <span
        className="block h-px flex-1"
        style={{
          maxWidth: ruleMaxWidth,
          background:
            'linear-gradient(to right, currentColor, transparent)',
        }}
      />
    </div>
  );
}

function Diamond({ size }: { size: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 10 10"
      width={size}
      height={size}
      aria-hidden="true"
    >
      <rect
        x="2"
        y="2"
        width="6"
        height="6"
        transform="rotate(45 5 5)"
        fill="currentColor"
      />
    </svg>
  );
}
